import _ from 'lodash';

import { Injectable } from '@angular/core';
import { WorkOrderControllerService } from '@app/generated/service';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { ChangeLogService, Subscription } from '@app/core/services/change-log.service';
import { SortOption } from './sort-option';
import { createSortOptions } from './sort-list-factory';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { WorkOrder } from './work-order.model';
import { TOGGLE_FAVOURITE_WORK_ORDER } from '@app/actions';
import { WorkOrderDetailsDto } from '@app/generated/model';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { Division } from '@app/constants';
import { createSearchIndex, SearchIndex } from '@app/utils';
import { LoaderService } from '@app/header/loader/loader.service';
import { ApiLoadErrorService } from '@app/core/services/api-load-error.service';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderService {
  private workOrders: WorkOrder[];
  private searchResult: WorkOrder[];
  private workOrderIndex: SearchIndex<WorkOrder>;
  private subscriptions: Subscription[];
  private selectedSortingOption: SortOption;
  private sortingOptions: SortOption[];
  private selectedSites: string[] = [];
  private selectedTaskNos: string[] = [];
  private lastLoadUsedOfflineFallback = false;
  private lastLoadBackendUnreachable = false;
  private lastOnlineLoadFailed = false;
  public searchText = '';
  constructor(
    private workOrderController: WorkOrderControllerService,
    private changeLogService: ChangeLogService,
    private localStorageService: LocalStorageService,
    private offlineData: OfflineDataDb,
    private loaderService: LoaderService,
    private apiLoadErrorService: ApiLoadErrorService,
  ) {}

  async load(): Promise<WorkOrder[]> {
    this.lastLoadUsedOfflineFallback = !navigator.onLine;
    this.lastLoadBackendUnreachable = !navigator.onLine;
    this.lastOnlineLoadFailed = false;

    try {
      if (navigator.onLine) {
        this.workOrders = await this.loadFromController();
        this.lastLoadUsedOfflineFallback = false;
      } else {
        // The device reports offline, so serve downloaded data. Still run the check-update
        // probe so LoaderService.backendReachable is confirmed unreachable and the offline
        // banner is driven by the same probe-based signal as the rest of the app instead of
        // navigator.onLine alone.
        this.workOrders = await this.loadFromDB();
        void this.loaderService.confirmBackendReachable();
      }
    } catch (error) {
      this.lastOnlineLoadFailed = navigator.onLine;
      const isUnauthorized = error instanceof Error && error.message === 'User unauthorized for this project';

      if (isUnauthorized) {
        // A 403 is a reachable authorization failure, not an offline fallback.
        // Do not reuse downloaded work orders here; surface the authorization state instead.
        this.workOrders = [];
        this.lastLoadUsedOfflineFallback = false;
        throw error;
      } else {
        // Only fall back to downloaded (IndexedDB) work orders when the backend is genuinely
        // unreachable, confirmed by the same check-update probe that drives the offline banner.
        // A reachable server error (e.g. HTTP 500) must not silently present stale offline data,
        // so we surface an empty list instead and let the online view reflect the failure.
        const backendReachable = await this.loaderService.confirmBackendReachable();
        if (backendReachable) {
          console.error('Error loading work orders (backend reachable):', error);
          this.apiLoadErrorService.report('DASHBOARD', 'Some work orders could not be loaded.');
          this.workOrders = [];
          this.lastLoadUsedOfflineFallback = false;
        } else {
          console.error('Error loading work orders; backend unreachable, using downloaded data:', error);
          this.workOrders = await this.loadFromDB();
          this.lastLoadUsedOfflineFallback = true;
          this.lastLoadBackendUnreachable = true;
        }
      }
    }

    this.workOrders = this.workOrders || [];
    await this.applyOfflineState(this.workOrders);

    this.createIndex();
    if (this.workOrders.length) {
      this.sortingOptions = createSortOptions(this.workOrders[0].division as Division);
      const option = this.localStorageService.getItem<SortOption>('sortingOption');
      if (option != null) {
        this.selectedSortingOption = option;
      } else {
        this.selectedSortingOption = this.sortingOptions[0];
      }
      this.subscriptions = this.workOrders.map((wo) =>
        this.changeLogService.subscribe(wo.id, (e) => this.apply(this.workOrders, e)),
      );
      this.subscriptions.forEach((sub) => sub.replay());

      if (navigator.onLine && !this.lastLoadUsedOfflineFallback) {
        await this.updateOfflineVersion();
      }
    }

    return Promise.resolve(this.workOrders);
  }

  async updateOfflineVersion() {
    for (const wo of this.workOrders) {
      await this.offlineData.workOrderDataItems
        .where({ workOrderId: wo.id, type: 'WORK_ORDER' })
        .modify(
          (offlineVersion) =>
            (offlineVersion.data = {
              ...offlineVersion.data,
              ...{
                isFavourite: wo.isFavourite,
              },
            }),
        )
        .catch((e) => console.error(e));
    }
  }

  async loadFromController(): Promise<WorkOrder[]> {
    try {
      const workOrders = await this.workOrderController.getWorkOrders().toPromise();
      return workOrders.map((wo) => new WorkOrder(wo));
    } catch (error) {
      if (error.status === 403) {
        throw new Error('User unauthorized for this project');
      }
      throw error;
    }
  }

  async loadFromDB(): Promise<WorkOrder[]> {
    const entries = await this.offlineData.workOrderDataItems.where('type').equals('WORK_ORDER').toArray();
    const offlineWorkOrders = await this.offlineData.workOrders.toArray();
    const offlineWorkOrderMap = new Map(offlineWorkOrders.map((item) => [item.workOrderId, item]));
    const items = entries.map((i) => i.data as WorkOrderDetailsDto).map((wo) => new WorkOrder(wo));
    items.forEach((wo) => {
      const offlineVersion = offlineWorkOrderMap.get(wo.id);
      wo.downloaded = true;
      wo.lastSyncedAt = offlineVersion?.lastUpdated ? new Date(offlineVersion.lastUpdated) : null;
    });
    return items;
  }

  private async applyOfflineState(workOrders: WorkOrder[]): Promise<void> {
    const offlineWorkOrders = await this.offlineData.workOrders.toArray();
    const offlineWorkOrderMap = new Map(offlineWorkOrders.map((item) => [item.workOrderId, item]));

    workOrders.forEach((wo) => {
      const offlineVersion = offlineWorkOrderMap.get(wo.id);
      wo.downloaded = !!offlineVersion;
      wo.lastSyncedAt = offlineVersion?.lastUpdated ? new Date(offlineVersion.lastUpdated) : null;
    });
  }

  unload() {
    if (this.subscriptions) {
      this.subscriptions.forEach((sub) => sub.dispose());
      this.subscriptions = null;
    }
  }

  getAll(): WorkOrder[] {
    return this.workOrders;
  }

  didUseOfflineFallbackOnLastLoad(): boolean {
    return this.lastLoadUsedOfflineFallback;
  }

  didDetectBackendUnreachableOnLastLoad(): boolean {
    return this.lastLoadBackendUnreachable;
  }

  didFailOnlineLoadOnLastAttempt(): boolean {
    return this.lastOnlineLoadFailed;
  }

  get(): WorkOrder[] {
    if (this.searchText) {
      this.searchResult = this.workOrderIndex.search(this.searchText);
    } else {
      this.searchResult = [...this.workOrders];
    }

    if (this.selectedSites.length > 0) {
      this.searchResult = this.searchResult.filter((wo) => this.selectedSites.includes(wo.siteNo));
    }

    if (this.selectedTaskNos.length > 0) {
      this.searchResult = this.searchResult.filter((wo) => wo.taskNo && this.selectedTaskNos.includes(wo.taskNo));
    }

    this.sortWorkOrders();
    return this.searchResult;
  }

  setSelectedSortingOption(sortOption: SortOption): void {
    this.selectedSortingOption = sortOption;
    this.localStorageService.setItem('sortingOption', sortOption);
  }

  getSelectedSortingOption(): SortOption {
    return this.selectedSortingOption;
  }

  getSortingOptions(): SortOption[] {
    return this.sortingOptions;
  }

  setSelectedSites(sites: string[]): void {
    this.selectedSites = sites;
  }

  setSelectedTaskNos(taskNos: string[]): void {
    this.selectedTaskNos = taskNos;
  }

  private createIndex() {
    this.workOrderIndex = createSearchIndex(this.workOrders, ['workOrderNo', 'title', 'siteNo', 'taskNo']);
  }

  async toggleFavouriteWorkOrder(workOrderId) {
    const event = { workOrderId };
    await this.changeLogService.publish(TOGGLE_FAVOURITE_WORK_ORDER, event, [workOrderId]);
  }

  apply(data: WorkOrder[], event: ChangeLogEvent) {
    switch (event.type) {
      case TOGGLE_FAVOURITE_WORK_ORDER: {
        const finding = this.workOrders.find((workOrder) => workOrder.id === event.content.workOrderId);
        finding.isFavourite = !finding.isFavourite;
        break;
      }
    }
  }

  private sortWorkOrders() {
    if (this.selectedSortingOption) {
      this.searchResult = _.orderBy(
        this.searchResult,
        ['isFavourite', this.selectedSortingOption.field],
        ['desc', this.selectedSortingOption.direction],
      );
    }
  }
}
